import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { withLatestFrom } from 'rxjs/operators';

import {
  CreateActivity,
  CreateActivityFailure,
  CreateActivitySuccess,
  CreateFeature,
  CreateFeatureFailure,
  CreateFeatureSuccess,
  DeleteActivity,
  DeleteActivityFailure,
  DeleteActivitySuccess,
  DeleteFeature,
  DeleteFeatureFailure,
  DeleteFeatureSuccess,
  LoadFeatures,
  LoadFeaturesFailure,
  LoadFeaturesSuccess,
  RoadmapActionTypes,
  UpdateActivity,
  UpdateActivityFailure,
  UpdateActivitySuccess,
  UpdateFeature,
  UpdateFeatureFailure,
  UpdateFeatureSuccess,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { RoadmapService } from '../../services/roadmap.service';
import { Feature, FeatureActivity } from '@completion/models';
import { trackResource } from './resource-fetch.util';
import { getCurrentProject } from '@completion/selectors';

@Injectable()
export class RoadmapEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<State>,
    private readonly roadmapService: RoadmapService,
  ) {}

  loadFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadFeatures>(RoadmapActionTypes.LoadFeatures),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'getFeatures',
        serviceArgs: ([_, project]) => [project?.id],
        onSuccess: (features: Feature[]) => new LoadFeaturesSuccess(features),
        onError: (error) => new LoadFeaturesFailure(error),
      }),
    ),
  );

  createFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateFeature>(RoadmapActionTypes.CreateFeature),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'createFeature',
        serviceArgs: ([action, project]: [CreateFeature, any]) => [project?.id, action.feature],
        onSuccess: (feature: Feature) => new CreateFeatureSuccess(feature),
        onError: (error) => new CreateFeatureFailure(error),
      }),
    ),
  );

  updateFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateFeature>(RoadmapActionTypes.UpdateFeature),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'updateFeature',
        serviceArgs: ([action, project]: [UpdateFeature, any]) => [project?.id, action.feature],
        onSuccess: (feature: Feature) => new UpdateFeatureSuccess(feature),
        onError: (error) => new UpdateFeatureFailure(error),
      }),
    ),
  );

  deleteFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteFeature>(RoadmapActionTypes.DeleteFeature),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'deleteFeature',
        serviceArgs: ([action, project]: [DeleteFeature, any]) => [project?.id, action.featureId],
        onSuccess: (_: void, sourceArgs?: any[]) => {
          const [maybeAction] = sourceArgs || [];
          const action = (Array.isArray(maybeAction) ? maybeAction[0] : maybeAction) as DeleteFeature;
          return new DeleteFeatureSuccess(action.featureId);
        },
        onError: (error) => new DeleteFeatureFailure(error),
      }),
    ),
  );

  createActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateActivity>(RoadmapActionTypes.CreateActivity),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'createActivity',
        serviceArgs: ([action, project]: [CreateActivity, any]) => [project?.id, action.featureId, action.activity],
        onSuccess: (activity: FeatureActivity, sourceArgs?: any[]) => {
          const [maybeAction] = sourceArgs || [];
          const action = (Array.isArray(maybeAction) ? maybeAction[0] : maybeAction) as CreateActivity;
          return new CreateActivitySuccess(action.featureId, activity);
        },
        onError: (error) => new CreateActivityFailure(error),
      }),
    ),
  );

  updateActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateActivity>(RoadmapActionTypes.UpdateActivity),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'updateActivity',
        serviceArgs: ([action, project]: [UpdateActivity, any]) => [project?.id, action.featureId, action.activity],
        onSuccess: (activity: FeatureActivity, sourceArgs?: any[]) => {
          const [maybeAction] = sourceArgs || [];
          const action = (Array.isArray(maybeAction) ? maybeAction[0] : maybeAction) as UpdateActivity;
          return new UpdateActivitySuccess(action.featureId, activity);
        },
        onError: (error) => new UpdateActivityFailure(error),
      }),
    ),
  );

  deleteActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteActivity>(RoadmapActionTypes.DeleteActivity),
      withLatestFrom(this.store.select(getCurrentProject)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        service: this.roadmapService,
        method: 'deleteActivity',
        serviceArgs: ([action, project]: [DeleteActivity, any]) => [project?.id, action.featureId, action.activityId],
        onSuccess: (_: void, sourceArgs?: any[]) => {
          const [maybeAction] = sourceArgs || [];
          const action = (Array.isArray(maybeAction) ? maybeAction[0] : maybeAction) as DeleteActivity;
          return new DeleteActivitySuccess(action.featureId, action.activityId);
        },
        onError: (error) => new DeleteActivityFailure(error),
      }),
    ),
  );
}
